import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export const WIDGET_DEFS = [
  { id: 'equity-curve',      label: 'Equity Curve'     },
  { id: 'account-summary',   label: 'Account Summary'  },
  { id: 'daily-pnl',         label: 'PnL Calendar'     },
  { id: 'win-rate',          label: 'Win Rate'          },
  { id: 'performance-stats', label: 'Performance'       },
  { id: 'pnl-distribution',  label: 'PnL Distribution' },
  { id: 'trade-map',         label: 'Trade Map'         },
  { id: 'drawdown',          label: 'Drawdown'          },
  { id: 'cumulative-pnl',    label: 'Cumulative PnL'   },
  { id: 'position-exposure', label: 'Exposure'          },
  { id: 'active-positions',  label: 'Active Positions'  },
  { id: 'live-pnl',          label: 'Live PnL'          },
  { id: 'position-history',  label: 'Position History'  },
  { id: 'trade-history',     label: 'Trade History'     },
  { id: 'orders',            label: 'Orders'            },
  { id: 'order-book',        label: 'Order Book'        },
  { id: 'meme-activity',     label: 'Meme Activity'     },
  { id: 'instant-trade',     label: 'Instant Trade'     },
  { id: 'signals',           label: 'Signals'           },
  { id: 'chat',              label: 'Ask Algo'          },
] as const

interface AnalyticsWidgetsStore {
  visible: Record<string, boolean>
  docked: Record<string, 'left' | 'right' | 'bottom'>
  pinned: string[]
  dockSizes: { left: number; right: number; bottom: number }
  dockOrder: Record<string, number>
  activeChatId: string | null
  toggle: (id: string) => void
  hide: (id: string) => void
  show: (id: string) => void
  setDock: (id: string, side: 'left' | 'right' | 'bottom' | null) => void
  setDockSize: (side: 'left' | 'right' | 'bottom', size: number) => void
  reorderWidget: (id: string, direction: 'up' | 'down') => void
  pin: (id: string) => void
  unpin: (id: string) => void
  setActiveChatId: (id: string | null) => void
}

export const useAnalyticsWidgetsStore = create<AnalyticsWidgetsStore>()(
  persist(
    (set) => ({
      visible: {},
      docked: {},
      pinned: [],
      dockSizes: { left: 20, right: 20, bottom: 30 },
      dockOrder: {},
      activeChatId: null,
      toggle: (id) => set(s => ({ visible: { ...s.visible, [id]: !s.visible[id] } })),
      hide: (id) => set(s => ({ visible: { ...s.visible, [id]: false } })),
      show: (id) => set(s => ({ visible: { ...s.visible, [id]: true } })),
      setDock: (id, side) => set(s => {
        if (!side) {
          const next = { ...s.docked }
          delete next[id]
          return { docked: next }
        }
        return { docked: { ...s.docked, [id]: side } }
      }),
      setDockSize: (side, size) => set(s => ({
        dockSizes: { ...s.dockSizes, [side]: size },
      })),
      reorderWidget: (id, direction) => set(s => {
        const side = s.docked[id]
        if (!side) return s
        const sameColumn: string[] = WIDGET_DEFS
          .map(d => d.id as string)
          .filter(wid => s.visible[wid] && s.docked[wid] === side)
          .sort((a, b) => (s.dockOrder[a] ?? WIDGET_DEFS.findIndex(d => d.id === a)) - (s.dockOrder[b] ?? WIDGET_DEFS.findIndex(d => d.id === b)))
        const idx = sameColumn.indexOf(id)
        if (idx === -1) return s
        const swapIdx = direction === 'up' ? idx - 1 : idx + 1
        if (swapIdx < 0 || swapIdx >= sameColumn.length) return s
        const swapId = sameColumn[swapIdx]
        const orderA = s.dockOrder[id] ?? WIDGET_DEFS.findIndex(d => d.id === id)
        const orderB = s.dockOrder[swapId] ?? WIDGET_DEFS.findIndex(d => d.id === swapId)
        return { dockOrder: { ...s.dockOrder, [id]: orderB, [swapId]: orderA } }
      }),
      pin: (id) => set(s => {
        const pinned = s.pinned ?? []
        if (pinned.includes(id) || pinned.length >= 5) return s
        return { pinned: [...pinned, id] }
      }),
      unpin: (id) => set(s => ({ pinned: (s.pinned ?? []).filter(p => p !== id) })),
      setActiveChatId: (id) => set({ activeChatId: id }),
    }),
    {
      name: 'algoglide-analytics-widgets',
      merge: (persisted: unknown, current) => ({
        ...current,
        ...(persisted as object),
        pinned: (persisted as Record<string, unknown>)?.pinned as string[] ?? current.pinned ?? [],
        dockSizes: {
          ...current.dockSizes,
          ...((persisted as Record<string, unknown>)?.dockSizes as object ?? {}),
        },
        dockOrder: {
          ...current.dockOrder,
          ...((persisted as Record<string, unknown>)?.dockOrder as object ?? {}),
        },
      }),
    }
  )
)
